import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { trackPageVisit } from "../utils/analytics";
import LanguageSelector from "./LanguageSelector";
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";

const Home: React.FC = () => {
  const { t, ready } = useTranslation();
  const disableFeatureLangSel = false;

  useEffect(() => {
    trackPageVisit("home");
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center px-4 py-8 animate-fade-in">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Loading translations...
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Please wait while we load the language resources.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("home.title")}
        description={`${t("home.description") as string} - ${
          t("home.freeMetaDescription") as string
        }`}
        keywords={t("home.keywords") as string}
      />
      <div className="min-h-screen flex items-center justify-center px-4 py-8 animate-fade-in dark:bg-neutral-800">
        <div className="max-w-6xl mx-auto text-center">
          {/* Hero Section */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center">
              <img src="android-chrome-192x192.png" alt="Streak Spark logo" />
            </div>
            {/* <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div> */}

            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
              {t("app.name")}
            </h1>

            <p className="text-base md:text-lg text-neutral-500 dark:text-neutral-400 mb-4 max-w-3xl mx-auto leading-relaxed">
              {t("app.description")}
            </p>

            <p className="text-xl md:text-2xl font-semibold text-primary-600 dark:text-primary-400 mb-4 max-w-3xl mx-auto leading-relaxed">
              {t("home.tagline")}
            </p>

            {/* Free to Use Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg mb-4">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {t("app.freeToUse")}
            </div>

            <div className="glass-card rounded-2xl p-5 mb-6 max-w-3xl mx-auto border border-green-200/70 dark:border-green-700/60">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 mb-3">
                {t("home.androidUpdateBadge")}
              </div>
              <h2 className="text-lg md:text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("home.androidUpdateTitle")}
              </h2>
              <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-300 mb-4">
                {t("home.androidUpdateDesc")}
              </p>
              <Link
                to="/downloads"
                className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
              >
                {t("home.androidUpdateCta")}
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link
                to="/app"
                className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-glow hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
              >
                {t("home.getStarted")}
                <svg
                  className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
              <Link
                to="/guide"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 text-primary-600 dark:text-primary-400 font-semibold rounded-xl hover:bg-primary-600 hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              >
                {t("home.learnMore")}
              </Link>
              <Link
                to="/downloads"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {t("home.downloadApp")}
              </Link>
            </div>
          </div>

          {/* Features Grid */}
          <h2 className="text-2xl font-bold text-neutral-800 dark:text-neutral-200 mb-6">
            {t("home.featuresHeading")}
          </h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
            <div
              className="glass-card p-6 rounded-2xl hover:shadow-medium transition-all duration-300 hover:-translate-y-1 animate-slide-up"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mb-4 shadow-soft mx-auto">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("home.trackHabits")}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t("home.trackHabitsDesc")}
              </p>
            </div>

            <div
              className="glass-card p-6 rounded-2xl hover:shadow-medium transition-all duration-300 hover:-translate-y-1 animate-slide-up"
              style={{ animationDelay: "0.2s" }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center mb-4 shadow-soft mx-auto">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("home.manageTasks")}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t("home.manageTasksDesc")}
              </p>
            </div>

            <div
              className="glass-card p-6 rounded-2xl hover:shadow-medium transition-all duration-300 hover:-translate-y-1 animate-slide-up"
              style={{ animationDelay: "0.3s" }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-warning-500 to-warning-600 rounded-xl flex items-center justify-center mb-4 shadow-soft mx-auto">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("home.visualizeProgress")}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t("home.visualizeProgressDesc")}
              </p>
            </div>

            <div
              className="glass-card p-6 rounded-2xl hover:shadow-medium transition-all duration-300 hover:-translate-y-1 animate-slide-up"
              style={{ animationDelay: "0.4s" }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 shadow-soft mx-auto">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("home.journalingFeature")}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t("home.journalingFeatureDesc")}
              </p>
            </div>
          </div>

          {/* Stats Section */}
          {/* <div className="glass-card p-6 rounded-2xl max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1">10K+</div>
              <div className="text-xs text-neutral-600 dark:text-neutral-400">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary-600 mb-1">1M+</div>
              <div className="text-xs text-neutral-600 dark:text-neutral-400">Habits Tracked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-600 mb-1">500K+</div>
              <div className="text-xs text-neutral-600 dark:text-neutral-400">Streaks Built</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning-600 mb-1">4.9★</div>
              <div className="text-xs text-neutral-600 dark:text-neutral-400">User Rating</div>
            </div>
          </div>
        </div> */}

          {!disableFeatureLangSel ? (
            <>
              {/* Language Selector */}
              <div className="mt-12 flex justify-center">
                <LanguageSelector />
              </div>
            </>
          ) : null}

          <SocialHandles />
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Home;
